import { z } from 'zod/v4'

/**
 * Connection system for WorkspaceDO-based connections.
 *
 * This system provides workspace-scoped storage that supports multiple platforms
 * and authentication mechanisms without requiring schema migrations for new platforms.
 *
 * Key benefits:
 * - Workspace isolation: Each workspace manages its own connections
 * - Platform agnostic: JSON storage supports any third-party platform
 * - Flexible auth: Supports OAuth2, API keys, webhooks, and other auth methods
 * - No schema migrations: New platforms can be added without database changes
 */

/**
 * Supported third-party platforms for data source connections.
 *
 * @example
 * ```typescript
 * // Google Ads connection for advertising data
 * platform: ConnectionPlatforms.GoogleAds
 *
 * // Mailchimp connection for email marketing data
 * platform: ConnectionPlatforms.Mailchimp
 * ```
 */
export const ConnectionPlatforms = {
	/** Google Ads - Advertising platform for campaign and conversion data */
	GoogleAds: 'google_ads',
	/** Mailchimp - Email marketing platform for subscriber and campaign data */
	Mailchimp: 'mailchimp',
	/** Stripe - Payment processing platform for transaction and customer data */
	Stripe: 'stripe',
	/** HubSpot - CRM platform for customer and sales data */
	HubSpot: 'hubspot',
	/** Salesforce - Enterprise CRM platform for comprehensive customer data */
	Salesforce: 'salesforce',
} as const

/**
 * Authentication mechanisms supported by the connection system.
 *
 * Each auth type has its own data structure and handling requirements:
 * - OAuth2: Access tokens with optional refresh capability
 * - API Key: Simple API key authentication
 * - Basic Auth: Username/password authentication
 * - JWT: JSON Web Token authentication
 * - Webhook Secret: Secret-based webhook verification
 *
 * @example
 * ```typescript
 * // OAuth2 for Google Ads
 * auth_type: ConnectionAuthTypes.OAuth2
 * auth_data: {
 *   access_token: "ya29.a0...",
 *   refresh_token: "1//04...",
 *   expires_at: "2024-01-15T10:00:00Z"
 * }
 *
 * // API Key for Mailchimp
 * auth_type: ConnectionAuthTypes.ApiKey
 * auth_data: {
 *   api_key: "abc123...",
 *   server_prefix: "us19"
 * }
 * ```
 */
export const ConnectionAuthTypes = {
	/** OAuth2 authentication with access/refresh tokens */
	OAuth2: 'oauth2',
	/** API key authentication */
	ApiKey: 'api_key',
	/** Basic username/password authentication */
	BasicAuth: 'basic_auth',
	/** JSON Web Token authentication */
	JWT: 'jwt',
	/** Webhook secret for verifying incoming webhooks */
	WebhookSecret: 'webhook_secret',
} as const

/**
 * Connection status values for tracking connection health and lifecycle.
 *
 * Status transitions:
 * - pending → active (successful connection)
 * - pending → error (connection failed)
 * - active → expired (tokens/credentials expired)
 * - active → inactive (manually disabled)
 * - expired → active (credentials refreshed)
 * - error → active (issue resolved)
 *
 * @example
 * ```typescript
 * // New connection being set up
 * status: ConnectionStatus.Pending
 *
 * // Working connection with valid credentials
 * status: ConnectionStatus.Active
 *
 * // OAuth tokens expired, needs refresh
 * status: ConnectionStatus.Expired
 * ```
 */
export const ConnectionStatus = {
	/** Connection is being set up or awaiting activation */
	Pending: 'pending',
	/** Connection is active and working correctly */
	Active: 'active',
	/** Connection has been manually disabled */
	Inactive: 'inactive',
	/** Connection credentials have expired and need refresh */
	Expired: 'expired',
	/** Connection has encountered an error and needs attention */
	Error: 'error',
} as const

// ===== VALIDATORS =====

/** Zod validator for supported third-party platforms */
export const ConnectionPlatformValidator = z.enum([
	ConnectionPlatforms.GoogleAds,
	ConnectionPlatforms.Mailchimp,
	ConnectionPlatforms.Stripe,
	ConnectionPlatforms.HubSpot,
	ConnectionPlatforms.Salesforce,
])

/** Zod validator for supported authentication types */
export const ConnectionAuthTypeValidator = z.enum([
	ConnectionAuthTypes.OAuth2,
	ConnectionAuthTypes.ApiKey,
	ConnectionAuthTypes.BasicAuth,
	ConnectionAuthTypes.JWT,
	ConnectionAuthTypes.WebhookSecret,
])

/** Zod validator for connection status values */
export const ConnectionStatusValidator = z.enum([
	ConnectionStatus.Pending,
	ConnectionStatus.Active,
	ConnectionStatus.Inactive,
	ConnectionStatus.Expired,
	ConnectionStatus.Error,
])

// ===== AUTH DATA STRUCTURES =====

/**
 * OAuth2 authentication data structure.
 *
 * Used for platforms like Google Ads, Facebook, and other OAuth2-enabled services.
 * Supports access tokens with optional refresh capability and scope management.
 *
 * @example
 * ```typescript
 * const oauth2Auth: OAuth2AuthData = {
 *   access_token: "ya29.a0AfH6SMC...",
 *   refresh_token: "1//04tqk...",
 *   expires_at: "2024-01-15T10:00:00Z",
 *   scopes: ["https://www.googleapis.com/auth/adwords"],
 *   token_type: "Bearer"
 * }
 * ```
 */
export const OAuth2AuthDataValidator = z.object({
	/** Current access token for API requests */
	access_token: z.string(),
	/** Optional refresh token for renewing access tokens */
	refresh_token: z.string().optional(),
	/** ISO timestamp when the access token expires */
	expires_at: z.string().optional(),
	/** OAuth scopes granted to this connection */
	scopes: z.array(z.string()).optional(),
	/** Token type, typically "Bearer" */
	token_type: z.string().default('Bearer'),
})

/**
 * API Key authentication data structure.
 *
 * Used for platforms like Mailchimp, Stripe, and other API key-based services.
 * Supports additional metadata like server prefixes and API versions.
 *
 * @example
 * ```typescript
 * // Mailchimp API key with server prefix
 * const mailchimpAuth: ApiKeyAuthData = {
 *   api_key: "abc123def456-us19",
 *   server_prefix: "us19",
 *   api_version: "3.0"
 * }
 *
 * // Stripe API key
 * const stripeAuth: ApiKeyAuthData = {
 *   api_key: "sk_live_abc123..."
 * }
 * ```
 */
export const ApiKeyAuthDataValidator = z.object({
	/** The API key for authentication */
	api_key: z.string(),
	/** Server prefix for services like Mailchimp (e.g., "us19") */
	server_prefix: z.string().optional(),
	/** API version to use */
	api_version: z.string().optional(),
})

/**
 * Basic authentication data structure.
 *
 * Used for simple username/password authentication with legacy systems
 * or internal APIs that don't support modern auth methods.
 *
 * @example
 * ```typescript
 * const basicAuth: BasicAuthData = {
 *   username: "admin",
 *   password: "secure_password"
 * }
 * ```
 */
export const BasicAuthDataValidator = z.object({
	/** Username for authentication */
	username: z.string(),
	/** Password for authentication */
	password: z.string(),
})

/**
 * JWT (JSON Web Token) authentication data structure.
 *
 * Used for token-based authentication with modern APIs that support JWT.
 * Includes optional expiration tracking for token management.
 *
 * @example
 * ```typescript
 * const jwtAuth: JWTAuthData = {
 *   token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
 *   expires_at: "2024-01-15T10:00:00Z"
 * }
 * ```
 */
export const JWTAuthDataValidator = z.object({
	/** The JWT token */
	token: z.string(),
	/** ISO timestamp when the token expires */
	expires_at: z.string().optional(),
})

/**
 * Webhook secret authentication data structure.
 *
 * Used for verifying incoming webhooks from third-party services.
 * The secret is used to validate webhook signatures and ensure authenticity.
 *
 * @example
 * ```typescript
 * const webhookAuth: WebhookSecretAuthData = {
 *   secret: "whsec_abc123...",
 *   endpoint_url: "https://myapp.com/webhooks/stripe"
 * }
 * ```
 */
export const WebhookSecretAuthDataValidator = z.object({
	/** Secret key for webhook signature verification */
	secret: z.string(),
	/** Optional webhook endpoint URL */
	endpoint_url: z.string().optional(),
})

/**
 * Union validator for all supported authentication data structures.
 *
 * Automatically validates auth data based on the auth_type field and ensures
 * the correct structure is used for each authentication method.
 */
export const AuthDataValidator = z.union([
	OAuth2AuthDataValidator,
	ApiKeyAuthDataValidator,
	BasicAuthDataValidator,
	JWTAuthDataValidator,
	WebhookSecretAuthDataValidator,
])

// ===== CONNECTION VALIDATORS =====

/**
 * Complete connection validator for stored connections.
 *
 * Represents a full connection record as stored in WorkspaceDO with all
 * metadata, authentication data, and tracking information.
 *
 * @example
 * ```typescript
 * const connection: Connection = {
 *   id: "conn_123",
 *   workspace_id: "workspace_456",
 *   name: "Google Ads Account",
 *   platform: "google_ads",
 *   auth_type: "oauth2",
 *   status: "active",
 *   auth_data: {
 *     access_token: "ya29.a0...",
 *     refresh_token: "1//04...",
 *     expires_at: "2024-01-15T10:00:00Z",
 *     token_type: "Bearer"
 *   },
 *   platform_data: {
 *     account_id: "123-456-7890",
 *     account_name: "My Campaign Account"
 *   },
 *   created_by: "user_789",
 *   created_at: "2024-01-01T00:00:00Z",
 *   last_synced_at: "2024-01-14T12:00:00Z"
 * }
 * ```
 */
export const ConnectionValidator = z.object({
	/** Unique connection identifier */
	id: z.number(),
	/** Public UUID identifier */
	uuid: z.string(),
	/** ID of the workspace that owns this connection */
	workspace_id: z.string(),
	/** Human-readable name for the connection */
	name: z.string(),
	/** Third-party platform this connection integrates with */
	platform: ConnectionPlatformValidator,
	/** Authentication mechanism used by this connection */
	auth_type: ConnectionAuthTypeValidator,
	/** Current connection status */
	status: ConnectionStatusValidator,
	/** Authentication credentials and tokens */
	auth_data: AuthDataValidator,
	/** Platform-specific metadata (account info, settings, etc.) */
	platform_data: z.record(z.string(), z.unknown()).nullable().optional(),
	/** Connection-specific settings and preferences */
	settings: z.record(z.string(), z.unknown()).nullable().optional(),
	/** ID of the user who created this connection */
	created_by: z.string(),
	/** Timestamp when the connection was created */
	created_at: z.iso.datetime(),
	/** Timestamp when the connection was last updated */
	updated_at: z.iso.datetime().nullable(),
	/** Timestamp of the last successful data sync */
	last_synced_at: z.iso.datetime().nullable().optional(),
	/** Timestamp when the connection credentials expire */
	expires_at: z.iso.datetime().nullable().optional(),
})

/**
 * Validator for creating new connections.
 *
 * Omits auto-generated fields like ID and timestamps that are set by the system.
 * Used when creating new connections through the API.
 *
 * @example
 * ```typescript
 * const newConnection: CreateConnection = {
 *   workspace_id: "workspace_456",
 *   name: "My Google Ads",
 *   platform: "google_ads",
 *   auth_type: "oauth2",
 *   status: "pending",
 *   auth_data: {
 *     access_token: "ya29.a0...",
 *     refresh_token: "1//04...",
 *     token_type: "Bearer"
 *   },
 *   created_by: "user_789"
 * }
 * ```
 */
export const CreateConnectionValidator = ConnectionValidator.omit({
	id: true,
	uuid: true,
	created_at: true,
	updated_at: true,
	last_synced_at: true,
})

/**
 * Validator for updating existing connections.
 *
 * Makes all fields optional except ID, allowing partial updates.
 * Commonly used for status updates, credential refreshes, and settings changes.
 *
 * @example
 * ```typescript
 * // Update connection status
 * const statusUpdate: UpdateConnection = {
 *   id: "conn_123",
 *   status: "active",
 *   last_synced_at: "2024-01-15T10:00:00Z"
 * }
 *
 * // Refresh OAuth tokens
 * const tokenUpdate: UpdateConnection = {
 *   id: "conn_123",
 *   auth_data: {
 *     access_token: "ya29.new_token...",
 *     expires_at: "2024-01-16T10:00:00Z",
 *     token_type: "Bearer"
 *   }
 * }
 * ```
 */
export const UpdateConnectionValidator = ConnectionValidator.partial().required({
	id: true,
})

// ===== TYPE EXPORTS =====

/** Supported third-party platform identifiers */
export type ConnectionPlatform = z.infer<typeof ConnectionPlatformValidator>

/** Supported authentication mechanism identifiers */
export type ConnectionAuthType = z.infer<typeof ConnectionAuthTypeValidator>

/** Connection status values for tracking lifecycle and health */
export type ConnectionStatus = z.infer<typeof ConnectionStatusValidator>

/** Union type of all authentication data structures */
export type AuthData = z.infer<typeof AuthDataValidator>

/** Complete connection record with all metadata and tracking fields */
export type Connection = z.infer<typeof ConnectionValidator>

/** Data required to create a new connection (excludes auto-generated fields) */
export type CreateConnection = z.infer<typeof CreateConnectionValidator>

/** Data for updating an existing connection (all fields optional except ID) */
export type UpdateConnection = z.infer<typeof UpdateConnectionValidator>

// ===== INDIVIDUAL AUTH DATA TYPES =====

/** OAuth2 authentication data with access/refresh tokens */
export type OAuth2AuthData = z.infer<typeof OAuth2AuthDataValidator>

/** API key authentication data with optional metadata */
export type ApiKeyAuthData = z.infer<typeof ApiKeyAuthDataValidator>

/** Basic username/password authentication data */
export type BasicAuthData = z.infer<typeof BasicAuthDataValidator>

/** JWT token authentication data with optional expiration */
export type JWTAuthData = z.infer<typeof JWTAuthDataValidator>

/** Webhook secret authentication data for signature verification */
export type WebhookSecretAuthData = z.infer<typeof WebhookSecretAuthDataValidator>
